import { FormEvent, useRef, useState, useCallback, useEffect, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { jwtSendOtp, jwtVerifyOtp, jwtUpdateProfile } from "@/lib/jwt-auth-client";
import { useAuthModal } from "@/store/useAuthModal";
import { trackSignUp, trackTrialStart } from "@/shared/lib/gtm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/Dialog";

// ─── OTP 6-box input ───────────────────────────────────────────────────────
function OTPInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? "");

  function focus(i: number) {
    refs.current[i]?.focus();
    refs.current[i]?.select();
  }

  function handleKey(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (digits[i]) {
        const next = digits
          .map((d, idx) => (idx === i ? "" : d))
          .join("")
          .trimEnd();
        onChange(next);
      } else if (i > 0) {
        focus(i - 1);
        const next = digits
          .map((d, idx) => (idx === i - 1 ? "" : d))
          .join("")
          .trimEnd();
        onChange(next);
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && i > 0) {
      focus(i - 1);
    } else if (e.key === "ArrowRight" && i < 5) {
      focus(i + 1);
    }
  }

  function handleChange(i: number, raw: string) {
    const char = raw.replace(/\D/g, "").slice(-1);
    if (!char) return;
    const next = [...digits];
    next[i] = char;
    onChange(next.join("").slice(0, 6));
    if (i < 5) setTimeout(() => focus(i + 1), 0);
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted);
    const lastFilled = Math.min(pasted.length, 5);
    setTimeout(() => focus(lastFilled), 0);
  }

  return (
    <div className="flex gap-2 justify-center" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          onFocus={(e) => e.target.select()}
          className="ds-title w-9 h-12 sm:w-11 sm:h-14 text-center rounded-xl bg-muted text-foreground outline-none transition-all focus:bg-primary/10 focus:ring-2 focus:ring-primary/30 disabled:opacity-40 caret-transparent"
        />
      ))}
    </div>
  );
}

// ─── Google Logo SVG ────────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

// ─── Back arrow ─────────────────────────────────────────────────────────────
function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute left-5 top-5 text-muted-foreground hover:text-foreground transition-colors rounded-full p-1.5 hover:bg-muted"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M10.707 3.293a1 1 0 0 1 0 1.414L7.414 8l3.293 3.293a1 1 0 0 1-1.414 1.414l-4-4a1 1 0 0 1 0-1.414l4-4a1 1 0 0 1 1.414 0z" />
      </svg>
    </button>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export function AuthModal() {
  const navigate = useNavigate();
  const { isOpen, step, email, isNewUser, message, close, setStep, setEmail, setIsNewUser } =
    useAuthModal();

  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const emailRef = useRef<HTMLInputElement>(null);

  // Auto-focus email input when modal opens
  useEffect(() => {
    if (isOpen && step === "email") {
      setTimeout(() => emailRef.current?.focus(), 50);
    }
  }, [isOpen, step]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  function reset() {
    setOtp("");
    setName("");
    setError(null);
    setIsLoading(false);
    setResendCooldown(0);
  }

  function handleClose() {
    close();
    reset();
  }

  // ── Step 1: Email submit ──────────────────────────────────────────────────
  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setIsLoading(true);
    try {
      await jwtSendOtp(email.trim());
      setStep("otp");
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  // ── Step 2: OTP submit ────────────────────────────────────────────────────
  async function handleOtpSubmit(e?: FormEvent) {
    e?.preventDefault();
    if (otp.length < 6) return;
    setError(null);
    setIsLoading(true);
    try {
      const result = await jwtVerifyOtp(email.trim(), otp);
      const userName = result.user?.name ?? "";
      trackSignUp("email");
      if (!userName.trim()) {
        setIsNewUser(true);
        trackTrialStart(result.user?.id ?? email);
        setStep("profile");
      } else {
        handleClose();
        navigate("/explore", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code. Please try again.");
      setOtp("");
    } finally {
      setIsLoading(false);
    }
  }

  // Auto-submit when 6 digits filled
  useEffect(() => {
    if (step === "otp" && otp.length === 6 && !isLoading) {
      handleOtpSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, step]);

  // ── Step 3: Profile submit ─────────────────────────────────────────────────
  async function handleProfileSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setError(null);
    setIsLoading(true);
    try {
      await jwtUpdateProfile({ name: name.trim() });
      handleClose();
      navigate("/explore", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  }

  // ── Resend OTP ─────────────────────────────────────────────────────────────
  async function handleResend() {
    if (resendCooldown > 0) return;
    setError(null);
    setIsLoading(true);
    try {
      await jwtSendOtp(email.trim());
      setResendCooldown(60);
      setOtp("");
    } catch {
      setError("Failed to resend. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  // ── Google sign-in ─────────────────────────────────────────────────────────
  async function handleGoogle() {
    setError(null);
    try {
      trackSignUp("google");
      // Redirect to Google OAuth consent page; the callback will exchange the code
      // for JWT tokens via the Django backend.
      const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      // Canonical scopes — must match backend Flow; Google returns userinfo.* URLs not "email profile".
      const scope =
        "openid https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile";
      const googleUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&state=${encodeURIComponent("/explore")}`;
      window.location.href = googleUrl;
    } catch {
      setError("Google sign-in failed. Please try again.");
    }
  }

  const maskedEmail = email ? email.replace(/(.{2}).+(@.+)/, (_, a, b) => `${a}•••${b}`) : "";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-sm bg-card/95 backdrop-blur-[16px] p-0 shadow-ambient-lg overflow-hidden">
        <DialogTitle className="sr-only">Authentication</DialogTitle>
        {/* Gradient accent bar */}
        <div className="h-0.5 w-full bg-gradient-to-r from-primary to-primary/70" />

        <div className="relative px-8 py-8">
          {/* Step: email */}
          {step === "email" && (
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              <div>
                <h2 className="ds-title text-foreground">
                  {message ? "Almost there" : "Welcome"}
                </h2>
                <p className="ds-body-sm text-muted-foreground mt-1">
                  {message || "Sign in or create an account to continue."}
                </p>
              </div>

              {/* Google */}
              <button
                type="button"
                onClick={handleGoogle}
                className="ds-button-label w-full flex items-center justify-center gap-2.5 rounded-xl bg-muted px-4 py-3 text-foreground hover:bg-muted/80 transition-colors"
              >
                <GoogleIcon />
                Continue with Google
              </button>

              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-muted-foreground/20" />
                <span className="ds-caption text-muted-foreground">or use email</span>
                <div className="h-px flex-1 bg-muted-foreground/20" />
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="auth-email"
                  className="ds-caption text-muted-foreground uppercase"
                >
                  Email address
                </label>
                <input
                  id="auth-email"
                  ref={emailRef}
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  className="ds-input w-full rounded-xl bg-muted px-4 py-3 text-foreground placeholder-muted-foreground outline-none ring-0 transition-all focus:ring-2 focus:ring-primary/30 focus:bg-primary/5"
                />
              </div>

              {error && <p className="ds-body-sm text-destructive">{error}</p>}

              <button
                type="submit"
                disabled={isLoading || !email.trim()}
                className="ds-button-label w-full rounded-xl bg-primary px-4 py-3 text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? "Sending code…" : "Continue with email"}
              </button>

              <div className="flex items-start gap-2.5 px-1 mt-4">
                <input
                  type="checkbox"
                  id="auth-terms"
                  defaultChecked
                  required
                  className="mt-0.5 h-4 w-4 shrink-0 rounded bg-muted accent-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
                />
                <label htmlFor="auth-terms" className="ds-caption text-muted-foreground">
                  I agree to the{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    className="underline underline-offset-2 hover:text-foreground"
                  >
                    Terms of Service
                  </a>
                  {" and "}
                  <a
                    href="/privacy"
                    target="_blank"
                    className="underline underline-offset-2 hover:text-foreground"
                  >
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>
            </form>
          )}

          {/* Step: otp */}
          {step === "otp" && (
            <form onSubmit={handleOtpSubmit} className="space-y-6">
              <BackButton
                onClick={() => {
                  setStep("email");
                  setOtp("");
                  setError(null);
                }}
              />

              <div className="pt-2">
                <h2 className="ds-title text-foreground">Check your email</h2>
                <p className="ds-body-sm text-muted-foreground mt-1.5">
                  We sent a 6-digit code to{" "}
                  <span className="font-medium text-foreground">{maskedEmail}</span>
                </p>
              </div>

              <OTPInput value={otp} onChange={setOtp} disabled={isLoading} />

              {error && <p className="ds-body-sm text-center text-destructive">{error}</p>}

              <button
                type="submit"
                disabled={isLoading || otp.length < 6}
                className="ds-button-label w-full rounded-xl bg-primary px-4 py-3 text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? "Verifying…" : "Verify code"}
              </button>

              <div className="text-center">
                <span className="ds-body-sm text-muted-foreground">Didn&apos;t receive it? </span>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || isLoading}
                  className="ds-button-label text-primary hover:text-primary/80 disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}

          {/* Step: profile (new users) */}
          {step === "profile" && (
            <form onSubmit={handleProfileSubmit} className="space-y-5">
              <div>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-primary"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <h2 className="ds-title text-foreground">One last thing</h2>
                <p className="ds-body-sm text-muted-foreground mt-1">What should we call you?</p>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="auth-name"
                  className="ds-caption text-muted-foreground uppercase"
                >
                  Your name
                </label>
                <input
                  id="auth-name"
                  type="text"
                  autoFocus
                  autoComplete="name"
                  required
                  placeholder="Jane Smith"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError(null);
                  }}
                  className="ds-input w-full rounded-xl bg-muted px-4 py-3 text-foreground placeholder-muted-foreground outline-none transition-all focus:ring-2 focus:ring-primary/30 focus:bg-primary/5"
                />
              </div>

              {error && <p className="ds-body-sm text-destructive">{error}</p>}

              <button
                type="submit"
                disabled={isLoading || !name.trim()}
                className="ds-button-label w-full rounded-xl bg-primary px-4 py-3 text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? "Creating account…" : "Get started"}
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
